import React, { ReactNode } from "react";
import { Table, Spin, Empty, Alert, Pagination } from "antd";
import type { ColumnsType } from "antd/es/table";
import SearchComponent, { SearchComponentProps } from "./SearchComponent";

export interface DataTableProps<T> {
  // Title / Header
  title?: ReactNode;
  headerActions?: ReactNode;

  // Data & Loading
  dataSource: T[];
  columns: ColumnsType<T>;
  loading?: boolean;
  rowKey?: string | ((record: T) => string);

  // Search & Filters
  searchProps?: SearchComponentProps;

  // Pagination — use plain antd pagination to avoid URL-state conflicts when
  // multiple DataTables are rendered on the same page.
  paginationProps?: {
    page: number;
    limit: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };

  // Error Handling
  isError?: boolean;
  errorMessage?: string;

  // Custom empty state
  emptyText?: ReactNode;
  emptyAction?: ReactNode;

  // Table props override (if absolutely needed)
  tableProps?: any;
}

export function DataTable<T extends object>({
  title,
  headerActions,
  dataSource,
  columns,
  loading = false,
  rowKey = "id" as any,
  searchProps,
  paginationProps,
  isError = false,
  errorMessage = "Failed to load data",
  emptyText = "No data available.",
  emptyAction,
  tableProps,
}: DataTableProps<T>) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      {/* HEADER */}
      {(title || headerActions) && (
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
          <div>
            {typeof title === "string" ? (
              <p className="text-sm font-semibold text-slate-800">{title}</p>
            ) : (
              title
            )}
          </div>
          {headerActions && <div>{headerActions}</div>}
        </div>
      )}

      <div className="p-6">
        {/* FILTERS & SEARCH */}
        {searchProps && (
          <div className="mb-2">
            <SearchComponent {...searchProps} />
          </div>
        )}

        {/* ERROR STATE */}
        {isError && (
          <div className="mb-4">
            <Alert
              type="error"
              showIcon
              message="Error"
              description={errorMessage}
            />
          </div>
        )}

        {/* TABLE */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spin size="large" />
          </div>
        ) : dataSource.length === 0 && !isError ? (
          <div className="py-12">
            <Empty
              description={<span className="text-slate-500">{emptyText}</span>}
            >
              {emptyAction}
            </Empty>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table
              rowKey={rowKey}
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              {...tableProps}
            />
          </div>
        )}

        {/* PAGINATION — plain antd Pagination, no URL state */}
        {!loading &&
          !isError &&
          paginationProps &&
          paginationProps.total > 0 && (
            <div className="mt-4 flex justify-end">
              <Pagination
                current={paginationProps.page}
                pageSize={paginationProps.limit}
                total={paginationProps.total}
                showSizeChanger
                onChange={paginationProps.onChange}
                showTotal={(tot, range) => `${range[0]}-${range[1]} of ${tot}`}
              />
            </div>
          )}
      </div>
    </div>
  );
}

export default DataTable;
